import React from 'react';
import { Card, Statistic, Row, Col, Tabs, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

export type Summary = {
  successful: number;
  failed: number;
  total: number;
};

export type FailedRecord<T> = {
  index: number;
  error: string;
  data: T;
};

type BulkImportSummaryPreviewProps<T> = {
  summary: Summary;
  failedRecords?: FailedRecord<T>[];
};

const BulkImportSummaryPreview = <T,>({
  summary,
  failedRecords = [],
}: BulkImportSummaryPreviewProps<T>) => {
  const { t } = useTranslation();

  const failedRecordsColumns = [
    {
      title: t('common.record'),
      dataIndex: 'data',
      key: 'data',
      render: (data: Record<string, any>) => (
        <pre style={{ fontSize: '12px', margin: 0 }}>{JSON.stringify(data, null, 2)}</pre>
      ),
    },
    {
      title: t('common.error'),
      dataIndex: 'error',
      key: 'error',
      render: (error: string) => <Typography.Text type="danger">{error}</Typography.Text>,
    },
  ];

  const tabItems = [
    {
      key: 'summary',
      label: t('common.summary'),
      children: (
        <Row gutter={16}>
          <Col span={8}>
            <Statistic title={t('common.successful')} value={summary.successful} />
          </Col>
          <Col span={8}>
            <Statistic title={t('common.failed')} value={summary.failed} />
          </Col>
          <Col span={8}>
            <Statistic title={t('common.total')} value={summary.total} />
          </Col>
        </Row>
      ),
    },
    {
      key: 'failedRecords',
      label: t('common.failedRecords'),
      children: (
        <Table
          dataSource={failedRecords}
          columns={failedRecordsColumns}
          rowKey={(record) => `${record.index}`}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />
      ),
    },
  ];

  return (
    <Card className="shadow-lg">
      <Typography.Title level={5}>{t('crud.create.uploadSummary')}</Typography.Title>
      <Tabs defaultActiveKey="summary" items={tabItems} />
    </Card>
  );
};

export default BulkImportSummaryPreview;
